import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../api/client';
import { usePermission } from '../../hooks/usePermission';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Pagination from '../../components/common/Pagination';
import AdvancedSearch, { SearchField, SearchCondition, conditionsToQueryParams, queryParamsToConditions } from '../../components/common/AdvancedSearch';

interface Survey {
  id: string;
  title: string;
  description: string;
  status: string;
  responseCount: number;
  targetCount: number;
  deadline: string;
  createdAt: string;
}

const initialFormData = {
  title: '',
  description: '',
  deadline: '',
};

export default function SurveyList() {
  const { canCreate } = usePermission();
  const [searchParams, setSearchParams] = useSearchParams();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchConditions, setSearchConditions] = useState<SearchCondition[]>(() => {
    const saved = searchParams.get('conditions');
    return saved ? queryParamsToConditions(saved) : [];
  });
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  
  // ページネーション・ソート・表示件数（URLから取得）
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = parseInt(searchParams.get('pageSize') || '20');
  const setCurrentPage = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; });
  };
  const setPageSize = (size: number) => {
    setSearchParams(prev => { prev.set('pageSize', String(size)); prev.delete('page'); return prev; });
  };
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: searchParams.get('sortBy') || 'createdAt',
    direction: (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc',
  });

  // 詳細検索フィールド定義
  const searchFields: SearchField[] = useMemo(() => [
    { key: 'title', label: 'タイトル', type: 'text', placeholder: 'タイトルで検索' },
    { key: 'status', label: 'ステータス', type: 'select', options: [
      { value: 'draft', label: '下書き' },
      { value: 'published', label: '公開中' },
      { value: 'closed', label: '終了' },
    ]},
    { key: 'deadline', label: '締切日', type: 'date' },
    { key: 'createdAt', label: '作成日', type: 'date' },
    { key: 'responseCount', label: '回答数', type: 'number', placeholder: '件数' },
  ], []);

  useEffect(() => {
    fetchSurveys();
  }, [searchConditions, currentPage, pageSize, sortConfig]);

  // 検索条件をURLに保存（他パラメータを維持）
  useEffect(() => {
    setSearchParams(prev => {
      if (searchConditions.length > 0) {
        prev.set('conditions', conditionsToQueryParams(searchConditions));
      } else {
        prev.delete('conditions');
      }
      return prev;
    }, { replace: true });
  }, [searchConditions, setSearchParams]);

  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // 詳細検索条件を追加
      if (searchConditions.length > 0) {
        params.append('advancedSearch', JSON.stringify(searchConditions));
      }
      
      params.append('page', String(currentPage));
      params.append('pageSize', String(pageSize));
      params.append('sortBy', sortConfig.key);
      params.append('sortOrder', sortConfig.direction);
      
      const data = await api.get<{ items: Survey[]; total: number; totalPages: number }>(`/api/surveys?${params.toString()}`);
      setSurveys(Array.isArray(data?.items) ? data.items : []);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
      setSurveys([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (conditions: SearchCondition[]) => {
    setSearchConditions(conditions);
    setSearchParams(prev => { prev.delete('page'); return prev; });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || submitting) return;

    setSubmitting(true);
    try {
      await api.post('/api/surveys', formData);
      setShowModal(false);
      setFormData(initialFormData);
      fetchSurveys();
    } catch (error) {
      console.error('Failed to create survey:', error);
      toast.error('アンケートの作成に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'warning' | 'success' | 'danger'> = {
      draft: 'default',
      published: 'success',
      closed: 'danger',
    };
    const labels: Record<string, string> = {
      draft: '下書き',
      published: '公開中',
      closed: '終了',
    };
    return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">アンケート</h1>
        {canCreate('surveys') && (
          <Button onClick={() => setShowModal(true)} className="w-full sm:w-auto">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            アンケートを作成
          </Button>
        )}
      </div>

      {/* 詳細検索 */}
      <Card>
        <AdvancedSearch
          fields={searchFields}
          onSearch={handleSearch}
          quickSearchPlaceholder="タイトルで検索..."
          quickSearchField="title"
          initialConditions={searchConditions}
        />
        
        {/* 表示件数・ソート */}
        <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t dark:border-gray-700">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>表示件数:</span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="w-24"
            >
              <option value="10">10</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="1000">1000</option>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>並び替え:</span>
            <Select
              value={`${sortConfig.key}-${sortConfig.direction}`}
              onChange={(e) => {
                const [key, direction] = e.target.value.split('-');
                const next = { key, direction: direction as 'asc' | 'desc' };
                setSortConfig(next);
                setSearchParams(prev => { prev.set('sortBy', next.key); prev.set('sortOrder', next.direction); prev.delete('page'); return prev; }, { replace: true });
              }}
              className="w-40"
            >
              <option value="createdAt-desc">作成日（新しい順）</option>
              <option value="createdAt-asc">作成日（古い順）</option>
              <option value="title-asc">タイトル（A-Z）</option>
              <option value="title-desc">タイトル（Z-A）</option>
              <option value="deadline-asc">締切日（近い順）</option>
              <option value="deadline-desc">締切日（遠い順）</option>
            </Select>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : surveys.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500">
            アンケートがありません
          </div>
        ) : (
          surveys.map(survey => (
            <Link key={survey.id} to={`/surveys/${survey.id}`}>
              <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                <div className="flex items-start justify-between mb-3">
                  {getStatusBadge(survey.status)}
                  {survey.deadline && (
                    <span className="text-xs text-gray-500">
                      〆 {new Date(survey.deadline).toLocaleDateString('ja-JP')}
                    </span>
                  )}
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {survey.title}
                </h3>
                {survey.description && (
                  <p className="text-gray-500 text-sm line-clamp-2 mb-4">
                    {survey.description}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="text-sm">
                      <span className="font-medium text-blue-600">{survey.responseCount}</span>
                      <span className="text-gray-500"> / {survey.targetCount || '∞'} 回答</span>
                    </div>
                  </div>
                  {survey.targetCount > 0 && (
                    <div className="w-24 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{ width: `${Math.min((survey.responseCount / survey.targetCount) * 100, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              </Card>
            </Link>
          ))
        )}
      </div>

      {/* ページネーション */}
      <div className="flex justify-center">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      <Modal isOpen={showModal} onClose={() => { setShowModal(false); setFormData(initialFormData); }} title="アンケートを作成" size="lg">
        <form onSubmit={handleCreate} className="space-y-4">
          <Input label="タイトル" name="title" value={formData.title} onChange={handleChange} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">説明</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              placeholder="アンケートの説明を入力..."
            />
          </div>
          <Input label="締め切り" name="deadline" type="date" value={formData.deadline} onChange={handleChange} />
          <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => { setShowModal(false); setFormData(initialFormData); }} className="w-full sm:w-auto">
              キャンセル
            </Button>
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? '作成中...' : '作成'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

